import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { LikeButtonVariants } from '@atoms/LikeButton/LikeButton';
import Text from '@atoms/Text/Text';
import Tooltip, { TooltipPositionEnum } from '@atoms/Tooltip/Tooltip';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import {
  IHotspotButton,
  IHotspotContent,
  IHotspotItem,
} from '@models/IHotspotButton';
import { WishlistButtonContext } from '@utility/context/WishlistButtonContext';
import { brightnessByColour } from '@utility/helpers/brightnessByColour';
import { handleHexCode } from '@utility/helpers/handleHexCode';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { useCallback, useContext, useRef } from 'react';

const textLimit = 25;

export const HotspotContent = ({
  groupName,
  hotspotItems,
  systemLayout,
  onBlur,
}: IHotspotContent) => (
  <div
    className="tw-p-2"
    role="dialog"
    aria-label={groupName || hotspotItems[0]?.name}
  >
    {groupName && (
      <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
        <Text
          variant="label-12"
          className="tw-uppercase tw-text-grey-70"
          as="p"
        >
          {groupName}
        </Text>
        <Icon
          name="arrow-right"
          className="tw-my-2 tw-ml-2 tw-text-grey-40"
          size={20}
        />
      </div>
    )}
    <div className="tw-grid tw-min-w-52 tw-gap-1">
      {hotspotItems.map((hotspot) => (
        <div key={hotspot.id}>
          {systemLayout ? (
            <SystemHotspot hotspot={hotspot} onBlur={onBlur} />
          ) : (
            <DefaultHotspot hotspot={hotspot} onBlur={onBlur} />
          )}
        </div>
      ))}
    </div>
  </div>
);
const SystemHotspot = ({
  hotspot,
  onBlur,
}: {
  hotspot: IHotspotItem;
  onBlur?: (event: React.FocusEvent) => void;
}) => (
  <a
    href={hotspot?.link?.url}
    target={hotspot?.link?.target}
    className={
      'tw-flex tw-items-center tw-justify-between tw-gap-7 tw-bg-grey-10 tw-px-4 tw-py-3 hover:tw-bg-grey-30'
    }
    onBlur={onBlur}
  >
    <Text variant="label-12" className="tw-uppercase tw-text-grey-70">
      {hotspot.category}
    </Text>
    <Text variant="body-small" className="tw-text-grey-100">
      {hotspot.name}
    </Text>
  </a>
);

const DefaultHotspot = ({
  hotspot,
  onBlur,
}: {
  hotspot: IHotspotItem;
  onBlur?: (event: React.FocusEvent) => void;
}) => {
  const WishlistButton = useContext(WishlistButtonContext);

  return (
    <div className="tw-flex tw-gap-x-xxs tw-rounded-[4px] tw-bg-grey-10 tw-px-3 tw-py-3 tw-outline-grey-80 tw-transition-colors hover:tw-bg-grey-20">
      <a
        href={hotspot?.link?.url}
        target={hotspot?.link?.target}
        className="tw-flex tw-gap-x-xxs"
        onBlur={onBlur}
      >
        <div className="tw-my-auto tw-flex tw-h-10 tw-w-10">
          {hotspot?.image ? (
            <Image image={hotspot.image} classes="tw-my-auto" />
          ) : (
            <span
              className={classNames(
                'tw-h-full tw-w-full tw-bg-no-repeat',
                hotspot?.colourHexCode && 'tw-rounded-full',
                hotspot?.colourHexCode &&
                  brightnessByColour(handleHexCode(hotspot?.colourHexCode)) >
                    127 &&
                  'tw-border tw-border-grey-20'
              )}
              style={{
                backgroundImage: hotspot?.previewUrl
                  ? `url(${hotspot.previewUrl}?format=jpg&width=40&quality=70)`
                  : 'none',
                backgroundColor: hotspot?.previewUrl
                  ? 'unset'
                  : handleHexCode(hotspot.colourHexCode),
              }}
            />
          )}
        </div>
        <div className="tw-content-center">
          <Text
            variant="label-12"
            className="tw-uppercase tw-text-grey-70"
            as="p"
          >
            {truncateText(hotspot.category)}
          </Text>
          <Text
            variant="ds-paragraph"
            className="tw-block tw-max-w-[120px] tw-truncate tw-text-grey-100"
          >
            {truncateText(hotspot.name)}
          </Text>
        </div>
      </a>
      {WishlistButton && (hotspot.productCard ?? hotspot.colourCard) && (
        <div className="tw-z-10 tw-ml-auto tw-flex tw-items-center tw-justify-center">
          <WishlistButton
            card={hotspot.productCard ?? hotspot.colourCard}
            cardId={hotspot.link?.url}
            isCard={false}
            collection={
              hotspot.productCard ? CardTypeEnum.Products : CardTypeEnum.Colours
            }
            wishlistDrawerLabels={hotspot.wishlistDrawerLabels}
            buttonVariant={LikeButtonVariants.Bordered}
            gtmTrackingEvents={{
              heading: hotspot.name ?? '',
              standardNameId: hotspot.id,
            }}
          />
        </div>
      )}
    </div>
  );
};

const NewHotspotButton = (props: IHotspotButton) => {
  const {
    disableBlur,
    disabled,
    groupName,
    hideTooltipOnMobile,
    hotspotItems,
    id,
    isActive,
    onClick,
    onFocus,
    tooltipOverflowLeft = false,
    tooltipOverflowRight = false,
    tooltipPosition = TooltipPositionEnum.Bottom,
    tooltipShouldStack,
    systemLayout,
  } = props;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useMediaQuery(752);

  const clickInProgress = useRef(false);

  const handleToggleTooltip = () => {
    onClick();
    if (isActive && !disableBlur) {
      buttonRef.current?.blur();
    }
    clickInProgress.current = false;
  };

  const handleFocus = () => {
    if (!onFocus || clickInProgress.current) return;
    onFocus();
  };

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      if (disableBlur) return;
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (!tooltipRef.current?.contains(relatedTarget)) {
        onClick(false);
      }
    },
    [onClick, disableBlur]
  );
  // Stacked hotspots for video
  if (tooltipShouldStack) {
    return (
      <div className="tw-absolute tw-bottom-4 tw-right-4">
        <Tooltip
          isActive={isActive && !(hideTooltipOnMobile && isMobile)}
          overflowLeft={tooltipOverflowLeft}
          overflowRight={tooltipOverflowRight}
          position={TooltipPositionEnum.Top}
          buttonEl={
            <button
              id={id}
              onMouseDown={() => (clickInProgress.current = true)}
              ref={buttonRef}
              onClick={handleToggleTooltip}
              onFocus={handleFocus}
              onBlur={disableBlur ? undefined : handleBlur}
              className={classNames(
                'tw-relative tw-h-[30px] tw-w-[30px] tw-rounded-full tw-bg-white tw-outline-none tw-transition-opacity tw-duration-500 hover:tw-opacity-100',
                "after:tw-content-[' '] after:tw-absolute after:tw-left-1/2 after:tw-top-1/2 after:-tw-translate-x-1/2 after:-tw-translate-y-1/2",
                'after:tw-h-[30px] after:tw-w-[30px] after:tw-rounded-full after:tw-border after:tw-border-white after:tw-transition-all after:tw-duration-500',
                isActive
                  ? 'tw-opacity-100 after:tw-scale-[140%]'
                  : 'tw-opacity-50',
                disabled ? 'tw-pointer-events-none hover:tw-cursor-default' : ''
              )}
              aria-label="All hotspots"
              data-selected={isActive}
            >
              <Icon
                name="plus"
                size={10}
                className={classNames(
                  'tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transition-transform tw-duration-300',
                  isActive ? 'tw-rotate-45' : 'tw-rotate-0'
                )}
              />
            </button>
          }
        >
          <div ref={tooltipRef}>
            <div className="tw-grid tw-gap-4 tw-p-2">
              {groupName && (
                <Text
                  variant="label-12"
                  className="tw-uppercase tw-text-grey-70"
                  as="p"
                >
                  {groupName}
                </Text>
              )}
              {(hotspotItems as (IHotspotItem | IHotspotItem[])[]).map(
                (hotspotGroup, index) => {
                  const groupArray = (
                    Array.isArray(hotspotGroup) ? hotspotGroup : [hotspotGroup]
                  ) as IHotspotItem[];
                  return (
                    <div className="tw-grid tw-gap-1" key={index}>
                      {groupArray.map((hotspot: IHotspotItem) => (
                        <div key={hotspot.id}>
                          {systemLayout ? (
                            <SystemHotspot
                              hotspot={hotspot}
                              onBlur={handleBlur}
                            />
                          ) : (
                            <DefaultHotspot
                              hotspot={hotspot}
                              onBlur={handleBlur}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </Tooltip>
      </div>
    );
  }
  // Default single hotspot
  return (
    <Tooltip
      isActive={isActive && !(hideTooltipOnMobile && isMobile)}
      overflowLeft={tooltipOverflowLeft}
      overflowRight={tooltipOverflowRight}
      position={tooltipPosition}
      buttonEl={
        <button
          id={id}
          onMouseDown={() => (clickInProgress.current = true)}
          ref={buttonRef}
          onClick={handleToggleTooltip}
          onFocus={handleFocus}
          onBlur={disableBlur ? undefined : handleBlur}
          className={classNames(
            'tw-relative tw-h-[30px] tw-w-[30px] tw-rounded-full tw-bg-white tw-outline-none tw-transition-opacity tw-duration-500 hover:tw-opacity-100',
            "after:tw-content-[' '] after:tw-absolute after:tw-left-1/2 after:tw-top-1/2 after:-tw-translate-x-1/2 after:-tw-translate-y-1/2",
            'after:tw-h-[30px] after:tw-w-[30px] after:tw-rounded-full after:tw-border after:tw-border-white after:tw-transition-all after:tw-duration-500',
            isActive ? 'tw-opacity-100 after:tw-scale-[140%]' : 'tw-opacity-50',
            disabled ? 'tw-pointer-events-none hover:tw-cursor-default' : ''
          )}
          aria-label={groupName || hotspotItems[0]?.name}
          data-selected={isActive}
        >
          <Icon
            name="plus"
            size={10}
            className={classNames(
              'tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transition-transform tw-duration-300',
              isActive ? 'tw-rotate-45' : 'tw-rotate-0'
            )}
          />
        </button>
      }
    >
      <div ref={tooltipRef}>
        <HotspotContent {...props} onBlur={handleBlur} />
      </div>
    </Tooltip>
  );
};

const truncateText = (text: string) => {
  if (!text || text.length <= textLimit) return text;

  return `${text.substring(0, textLimit)}...`;
};

export default NewHotspotButton;
